import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import BreadCrumbArrow from '@/components/atom/icon/Arrow/BreadCrumbArrow';
import { Urls } from '@/constants/url';
import { zIndex } from '@/constants/zIndex';
import { Tablet } from '@/styles/layout';
import { ROUTES } from '@/types/common';
import { MagazineType } from '@/types/magazine';
import { getPageName, logger } from '@/utils/logger';

interface Props {
  visible: boolean;
  path: string;
  type: string;
  name?: string;
  urlParameter?: string;
}

const BreadCrumbs = ({ visible, path, type, name, urlParameter }: Props) => {
  const router = useRouter();

  const paths = path.split('/').filter((item) => item !== 'content');

  const breadCrumbPath = useCallback(
    (detailPath: string) => {
      if (detailPath === '') {
        return {
          label: '홈',
          url: '/',
          logName: 'home',
        };
      }

      if (path.includes('community')) {
        if (detailPath === 'setting') {
          return {
            label: '설정',
            url: `${ROUTES.COMMUNITY.SETTING.path}`,
            logName: 'community_setting',
          };
        }

        if (detailPath === 'write') {
          return {
            label: '글쓰기',
            url: `${ROUTES.COMMUNITY.WRITE.path}`,
            logName: 'community_write',
          };
        }

        if (detailPath === 'comment') {
          const pid = path.split('/');
          return {
            label: '대댓글',
            url: `${ROUTES.COMMUNITY.COMMENT.path}/${pid[pid.length - 1]}`,
            logName: 'community_comment',
          };
        }

        if (detailPath === 'community') {
          return {
            label: '커뮤니티',
            url: `${ROUTES.COMMUNITY.LIST.path}`,
            logName: 'community_list',
          };
        }

        if (detailPath === 'list') {
          return {
            label: name,
            url: `${ROUTES.COMMUNITY.LIST.path}${urlParameter}`,
            logName: 'community_list_category',
          };
        }
      }

      if (detailPath === 'qna') {
        return {
          label: '실시간 의료상담',
          url: ROUTES.QNA.REALTIME.path,
          logName: 'qna_list',
        };
      }

      if (detailPath === 'realtime') {
        return {
          label: name,
          url: `${ROUTES.QNA.REALTIME.path}${urlParameter}`,
          logName: 'qna_list_category',
        };
      }

      if (detailPath === 'write') {
        return {
          label: '질문 작성',
          url: ROUTES.QNA.WRITE.path,
          logName: 'qna_write',
        };
      }

      if (detailPath === 'nps') {
        return {
          label: '의견 전하기',
          url: ROUTES.QNA.NPS.path,
          logName: 'qna_nps',
        };
      }

      if (path.includes('magazine')) {
        if (detailPath === 'magazine') {
          return {
            label: '건강 매거진',
            url: Urls.Magazine,
            logName: 'magazine_list',
          };
        }

        return {
          label: name,
          url: `${ROUTES.MAGAZINE.DETAIL.path}/${urlParameter}`,
          logName: 'magazine_list',
        };
      }

      if (path.includes('faq')) {
        if (detailPath === 'faq') {
          return {
            label: '1분 FAQ',
            url: ROUTES.FAQ.LIST.path,
            logName: 'faq_list',
          };
        }

        return {
          label: name,
          url: `${ROUTES.FAQ.DETAIL.path}/${urlParameter}`,
          logName: 'faq_list',
        };
      }

      if (detailPath === 'mypage') {
        return {
          label: '마이페이지',
          url: ROUTES.COMMON.MYPAGE.path,
          logName: 'mypage',
        };
      }

      if (path.includes('hospitals')) {
        if (detailPath.includes('search')) {
          return {
            label: '검색 결과',
            url: `${ROUTES.HOSPITAL.DETAIL.path}${detailPath}`,
            logName: 'hospitals_search',
          };
        }
        if (detailPath.includes('sub-category')) {
          return {
            label: '검색 결과',
            url: `${ROUTES.HOSPITAL.HOME.path}/${detailPath}`,
            logName: 'hospitals_subcategory',
          };
        }

        if (detailPath.includes('neighborhood')) {
          return {
            label: '지역별 병원 정보',
            url: path,
            logName: 'hospitals_neighborhood',
          };
        }

        return {
          label: '병원 찾기',
          url: `${ROUTES.HOSPITAL.HOME.path}`,
          logName: 'hospitals',
        };
      }

      if (detailPath === 'search') {
        return {
          label: '통합검색',
          url: `${ROUTES.SEARCH().HOME.path}`,
          logName: 'integrated_search',
        };
      }

      if (detailPath === 'list') {
        return {
          label: '전체',
          url: `${ROUTES.MAGAZINE.LIST.path}/${MagazineType.INTERNAL}`,
          logName: 'magazine_list',
        };
      }

      if (detailPath.includes('tag')) {
        const tag = detailPath.split('=');
        return {
          label: decodeURI(tag[1]),
          url: `${ROUTES.MAGAZINE.HOME.path}/${detailPath}`,
          logName: 'magazine_list_tags',
        };
      }

      if (detailPath === 'non-benefit') {
        return {
          label: '우리동네 진료 가격',
          url: `${ROUTES.NON_BENEFIT.HOME.path}`,
          logName: 'non_benefit_home',
        };
      }

      if (detailPath.includes('map')) {
        return {
          label: '최저가 약국 찾기',
          url: `${ROUTES.NON_BENEFIT.MAP.path}`,
          logName: 'non_benefit_map',
        };
      }

      if (name) {
        return {
          label: name,
          url: type === 'qna' ? `${Urls.QnaCategory}/${detailPath}` : `${Urls.MagazineCategory}/list/${detailPath}`,
          logName: 'ctg',
        };
      }

      return {
        label: decodeURI(decodeURIComponent(detailPath)),
        url: `${ROUTES.MAGAZINE.LIST.path}/${detailPath}`,
        logName: 'magazine_list_tags',
      };
    },
    [name, path, type],
  );

  const breadCrumb = useMemo(() => {
    return paths.map((path) => {
      return breadCrumbPath(path);
    });
  }, [breadCrumbPath, paths]);

  const onClickBreadCrumb = (name: string) => {
    logger({
      pageName: getPageName(router),
      eventName: `breadcrumb_${name}_click`,
    });
  };

  return (
    <Wrapper visible={visible} id="breadcrumbs">
      {breadCrumb.map((item, index) => (
        <Path role="button" key={item.label} onClick={() => onClickBreadCrumb(item.logName)}>
          <Link href={item.url}>
            <a>
              <p>{item.label}</p>
            </a>
          </Link>
          {index !== breadCrumb.length - 1 && <BreadCrumbArrow />}
        </Path>
      ))}
    </Wrapper>
  );
};

export default BreadCrumbs;

const Wrapper = styled.div<{ visible: boolean }>`
  ${({ theme }) => theme.typography.UI13M};
  color: ${({ theme }) => theme.color.gray400};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  position: ${({ visible }) => (visible ? 'static' : 'absolute')};
  display: flex;

  @media screen and (max-width: ${Tablet}) {
    ${({ theme }) => theme.typography.UI14R};
  }
`;

const Path = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin: 0 2px;
  }
  p:hover {
    text-decoration: underline;
  }
`;

const BreadCrumbsWrapper = styled.div`
  padding: 24px 24px 0;
  position: relative;
  z-index: ${zIndex.BreadCrumb};
`;

BreadCrumbs.Wrapper = BreadCrumbsWrapper;
